/* eslint-disable no-param-reassign */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// core
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from '@material-ui/core';

import clsx from 'clsx';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import { EDIT_MODE, VIEW_MODE } from '../../../utils/const/taskModes';

import useStyles from '../styles';
import QuestionShell from '../QuestionShell';
import useQuestionForm, { QUESTION_FIELDS } from '../QuestionShell/useQuestionForm';
import { removeChoiceFromQuestionLogic } from '../../../utils/questionLogic';

function MultipleSelection({
  identifier,
  pages,
  info,
  handleDeleteQuestion,
  mode,
  handleDuplicateQuestion,
  setPages,
}) {
  const styles = useStyles();
  const { t } = useTranslation();

  const initialId = 1;
  const [correct, setCorrect] = useState([]);
  const [correctIds, setCorrectIds] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [correctAnswers, setCorrectAnswers] = useState([]);
  const [answerChecked, setAnswerChecked] = useState([]);
  const [options, setOptions] = useState([]);

  const question = useQuestionForm({
    identifier,
    pages,
    info,
    setPages,
    writeVia: 'setPages',
    // El blur escribe mutando: con `setPages` se recrearía `pages` y el efecto de
    // carga volvería a resetear opciones y respuestas marcadas.
    blurVia: 'pages',
    autoLoadMedia: false,
  });
  const {
    image, setImage, setEmailsArray, setSendEmail,
    scheduleWrite, questionLogic, syncLocalField,
  } = question;

  useEffect(() => {
    if (info !== undefined) {
      setImage({
        ...image,
        url: info.imageUrl,
        name: info.imageKey,
      });
      if (
        info.QuestionEmails !== undefined
        && info.QuestionEmails.length !== 0
      ) {
        setEmailsArray(info.QuestionEmails[0].emails.split(','));
      }
      setSendEmail(info.sendEmail);
      const corrects = info.CorrectQuestionChoices;
      setCorrectIds(corrects.map((x) => x.questionChoiceId));
      const checkedCorrect = {};
      const sortedChoices = [
        ...info.QuestionChoices,
      ].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      // eslint-disable-next-line array-callback-return
      sortedChoices.map((x) => {
        if (correctIds.includes(x.id)) {
          checkedCorrect[x.id - 1] = true;
        } else {
          checkedCorrect[x.id - 1] = false;
        }
      });
      setAnswerChecked(checkedCorrect);
      setCorrectAnswers(correctIds);
      setOptions(sortedChoices);
    } else if (pages && pages[identifier - 1].questionChoices) {
      const { questionChoices } = pages[identifier - 1];
      // Las borradas se saltean: siguen en la página con su flag para que el API
      // las dé de baja, pero no vuelven a la lista editable.
      const optionsArray = [];
      let identifierChoice = 0;
      questionChoices.filter((choice) => !choice.deleted).forEach((questionChoice) => {
        identifierChoice += 1;
        optionsArray.push({
          title: questionChoice.title,
          type: questionChoice.type,
          identifierOrder: `${identifierChoice}`,
          identifier: identifierChoice,
          id: questionChoice.id,
        });
      });
      if (pages[identifier - 1].correctQuestionChoices
        && pages[identifier - 1].correctQuestionChoices.length > 0) {
        const arrayAnswerChecked = [];
        const correctArray = [...correct];
        const correctsId = pages[identifier - 1].correctQuestionChoices.map(
          (x) => x.questionChoiceId,
        );
        optionsArray.forEach((option) => {
          if (correctsId.includes(option.id)) {
            arrayAnswerChecked[option.identifier - 1] = true;
            correctArray.push(option.identifierOrder);
          } else {
            arrayAnswerChecked[option.identifier - 1] = false;
          }
        });
        setAnswerChecked(arrayAnswerChecked);
        setCorrect(correctArray);
        pages[identifier - 1] = {
          ...pages[identifier - 1],
          questionAnswer: correctArray,
        };
      }
      setOptions(optionsArray);
      setSendEmail(pages[identifier - 1].sendEmail);
      if (
        pages[identifier - 1].questionEmails !== undefined
        && pages[identifier - 1].questionEmails.length !== 0
      ) {
        setEmailsArray(
          pages[identifier - 1].questionEmails[0].emails.split(','),
        );
      }
    } else {
      if (
        pages[identifier - 1].questionEmails !== undefined
        && pages[identifier - 1].questionEmails.length !== 0
      ) {
        setEmailsArray(
          pages[identifier - 1].questionEmails[0].emails.split(','),
        );
      }
      setOptions([]);
    }

    if (pages && pages[identifier - 1].imageUrl) {
      setImage({
        ...image,
        url: pages[identifier - 1].imageUrl,
        name: pages[identifier - 1].imageKey,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages]);

  // Las opciones borradas se conservan en `questionChoices` con `deleted: true`
  // para que el API sepa cuáles dar de baja.
  const deletedChoices = () => (pages[identifier - 1].questionChoices || [])
    .filter((choice) => choice.deleted);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const newOptions = Array.from(options);
    const [movedItem] = newOptions.splice(result.source.index, 1);
    newOptions.splice(result.destination.index, 0, movedItem);

    const updatedOptions = newOptions.map((opt, index) => ({
      ...opt,
      identifier: index + 1,
      position: index,
    }));

    setOptions(updatedOptions);
    pages[identifier - 1] = {
      ...pages[identifier - 1],
      questionChoices: updatedOptions,
    };
  };

  const handleAddOption = () => {
    if (options.length && !options[options.length - 1].title) return;
    const generatedId = initialId + options.length;
    const newOptions = [
      ...options,
      {
        identifier: generatedId,
        identifierOrder: `${options.length + 1}`,
        title: '',
        type: 'text',
        position: options.length,
      },
    ];
    newOptions.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    setOptions(newOptions);
    setAnswerChecked((prev) => {
      const newChecked = [...prev];
      newChecked[generatedId - 1] = false;
      return newChecked;
    });

    pages[identifier - 1] = {
      ...pages[identifier - 1],
      questionChoices: [...newOptions, ...deletedChoices()],
    };
  };

  const handleAddOtherOption = () => {
    if (options.length && !options[options.length - 1].title) return;

    const generatedId = initialId + options.length;

    const newOption = {
      identifier: `${generatedId}`,
      title: t('task.taskEdition.simpleSelectionText.other'),
      type: 'other',
      position: options.length,
    };

    const updatedOptions = [...options, newOption];
    updatedOptions.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    setOptions(updatedOptions);

    pages[identifier - 1] = {
      ...pages[identifier - 1],
      questionChoices: [...updatedOptions, ...deletedChoices()],
      questionAnswer: correct,
    };
  };

  const handleOnChange = useCallback((event) => {
    const { id, value, imageUrl } = event.target;

    syncLocalField(id, value);

    if (QUESTION_FIELDS.includes(id)) {
      scheduleWrite({
        [id]: value,
        questionLogic,
        imageUrl,
      });
      return;
    }

    const indexOption = options.findIndex(
      (option) => `${option.identifier}` === id,
    );
    if (indexOption === -1) return;

    const newOptions = [...options];
    newOptions[indexOption] = { ...newOptions[indexOption], title: value };
    setOptions(newOptions);

    scheduleWrite({ questionChoices: [...newOptions, ...deletedChoices()] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, questionLogic, scheduleWrite, syncLocalField]);

  const handleCorrectAnswer = (event, identifierid) => {
    const correctArray = [...correct];
    if (event.target.checked) {
      if (correctArray.includes(identifierid)) {
        const index = correct.indexOf(identifierid);
        if (index > -1) {
          correctArray.splice(index, 1); // 2nd parameter means remove one item only
          setAnswerChecked((prevState) => {
            const newState = [...prevState];
            newState[identifierid - 1] = false;
            return newState;
          });
        }
      } else {
        correctArray.push(identifierid);
        setAnswerChecked((prevState) => {
          const newState = [...prevState];
          newState[identifierid - 1] = true;
          return newState;
        });
      }
    } else {
      const index = correctArray.indexOf(identifierid);
      correctArray.splice(index, 1); // 2nd parameter means remove one item only
      setAnswerChecked((prevState) => {
        const newState = [...prevState];
        newState[identifierid - 1] = false;
        return newState;
      });
    }
    setCorrect(correctArray);
    pages[identifier - 1] = {
      ...pages[identifier - 1],
      questionAnswer: correctArray,
    };
  };

  const isCorrect = (identifierid) => {
    if (!correct) return false;
    const id = Number.parseInt(identifierid, 10);
    return correct.includes(id) || correctIds.includes(id) || correctAnswers.includes(id);
  };

  const handleDeleteOption = (id) => {
    const optionToDelete = options.find(
      (opt) => (
        opt.id?.toString() === id?.toString() || opt.identifier?.toString() === id?.toString()
      ),
    );

    const remainingOptions = options.filter(
      (opt) => (
        opt.id?.toString() !== id?.toString() && opt.identifier?.toString() !== id?.toString()
      ),
    ).map((opt, index) => ({
      ...opt,
      identifier: index + 1,
      identifierOrder: `${index + 1}`,
    }));

    setOptions(remainingOptions);

    const deleted = deletedChoices();
    // Los identificadores de las alternativas se reasignan, así que las
    // conexiones que apuntaban a ellas hay que renumerarlas.
    const questionLogicWithoutChoice = removeChoiceFromQuestionLogic(
      pages[identifier - 1].questionLogic, optionToDelete?.identifier,
    );

    if (optionToDelete?.id) {
      pages[identifier - 1] = {
        ...pages[identifier - 1],
        questionLogic: questionLogicWithoutChoice,
        questionChoices: [
          ...remainingOptions,
          ...deleted.filter((choice) => choice.id !== optionToDelete.id),
          { ...optionToDelete, deleted: true },
        ],
      };
    } else {
      pages[identifier - 1] = {
        ...pages[identifier - 1],
        questionLogic: questionLogicWithoutChoice,
        questionChoices: [...remainingOptions, ...deleted],
      };
    }
  };

  return (
    <QuestionShell
      {...question}
      handleOnChange={handleOnChange}
      identifier={identifier}
      info={info}
      mode={mode}
      titleKey="task.taskEdition.multipleSelectionText.question"
      descriptionKey="task.taskEdition.multipleSelectionText.description"
      handleDeleteQuestion={handleDeleteQuestion}
      handleDuplicateQuestion={handleDuplicateQuestion}
    >
      <DragDropContext onDragEnd={handleDragEnd}>
        <div onDragStart={(e) => e.stopPropagation()}>
          <Droppable droppableId="options">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                {options.map((option, index) => (
                  <Draggable
                    key={option.identifier || `${option.id}`}
                    draggableId={`${option.identifier || option.id}`}
                    index={index}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          ...provided.draggableProps.style,
                          marginBottom: '8px',
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: 'white',
                          cursor: 'grab',
                        }}
                      >
                        <FormControlLabel
                          className={styles.formControlLabel}
                          control={
                            <Checkbox
                              className={styles.checkbox}
                              checkedIcon={
                                <CheckBoxIcon style={{ color: 'green' }} />
                              }
                              name={`${option.identifier}`}
                              onClick={(event) => handleCorrectAnswer(
                                event,
                                option.identifier,
                              )}
                              checked={
                                mode === VIEW_MODE
                                  ? isCorrect(
                                    option.identifier || `${option.id}`,
                                  )
                                  : answerChecked[option.identifier - 1]
                              }
                              disabled={mode === VIEW_MODE}
                            />
                          }
                          label={
                            option.type === 'other' ? (
                              <Box
                                display="flex"
                                alignItems="center"
                                width="100%"
                              >
                                <Typography className={styles.otherLabel}>
                                  {t('task.taskEdition.simpleSelectionText.other')}:
                                </Typography>
                                <TextField
                                  id={`${option.identifier}`}
                                  variant="standard"
                                  InputProps={{ disableUnderline: true }}
                                  disabled
                                />
                              </Box>
                            ) : (
                              <div className={styles.optionContainer}>
                                <TextField
                                  id={`${option.identifier || option.id}`}
                                  className={styles.optionInput}
                                  value={option.title}
                                  placeholder={`${t(
                                    'task.taskEdition.multipleSelectionText.write',
                                  )} ${t('task.taskEdition.multipleSelectionText.option')}`}
                                  InputProps={{ disableUnderline: true }}
                                  onChange={mode === EDIT_MODE ? handleOnChange : null}
                                  disabled={mode === VIEW_MODE}
                                  multiline
                                  maxRows={5}
                                  fullWidth
                                  style={{ pointerEvents: 'auto', cursor: 'text' }}
                                />
                                <button
                                  className={styles.deleteOptionButton}
                                  onClick={
                                    () => handleDeleteOption(option.identifier || option.id)
                                  }
                                  style={{ display: mode === VIEW_MODE ? 'none' : 'flex' }}
                                >
                                  &times;
                                </button>
                              </div>
                            )
                          }
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </DragDropContext>
      <Button
        onClick={handleAddOption}
        className={clsx({
          [styles.hide]: info,
        })}
      >
        {t('task.taskEdition.simpleSelectionText.addOption')}
      </Button>

      <Button
        onClick={handleAddOtherOption}
        className={clsx({
          [styles.hide]: info,
        })}
        disabled={options.some((opt) => opt.type === 'other')}
      >
        {t('task.taskEdition.simpleSelectionText.otherOption')}
      </Button>
    </QuestionShell>
  );
}

export default MultipleSelection;
